/**
 * Cliente para el endpoint unificado de cotización.
 *
 * ÚNICA fuente de verdad: NINGUNA calculadora debe calcular localmente.
 * Todas (Home, App Cliente, Admin, Oficina, Bot) consumen este módulo.
 *
 * Endpoint backend: GET /api/quote/calculate
 * Garantías:
 *   - Tasa cacheada 5 min en el servidor (todas ven el mismo número)
 *   - Tramos escalonados aplicados por el motor central
 *   - Monto destino SIEMPRE entero (truncado hacia abajo)
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Calcula el monto destino dado un monto origen.
 *
 * @param {Object} params
 * @param {number} params.amount - Monto a enviar (en divisa origen)
 * @param {string} params.origin - Divisa origen (CLP, BOB)
 * @param {string} params.destination - Divisa destino (BOB, CLP, ARS, PEN, COP, VES)
 * @param {string} [params.sessionId] - Si se provee y hay lock activo, devuelve la tasa congelada
 * @returns {Promise<Object>} Resultado del backend con amount_destination entero
 */
export async function quoteCalculate({ amount, origin = 'CLP', destination = 'BOB', sessionId = null }) {
  const url = new URL(`${API}/quote/calculate`);
  url.searchParams.set('amount', amount);
  url.searchParams.set('origin', origin);
  url.searchParams.set('destination', destination);
  if (sessionId) url.searchParams.set('session_id', sessionId);
  const res = await axios.get(url.toString(), { timeout: 15000 });
  return res.data;
}

/**
 * Calcula el monto origen dado un monto destino deseado.
 *
 * Si NO pasas `rounding`, el backend usa `rounding=1` (peso entero) para
 * todas las divisas → redondeo al monto MÁS CERCANO, sin sesgo al alza.
 * Pasar un `rounding` mayor sólo si quieres sugerir montos "redondos"
 * comerciales (p.ej. 1000 para múltiplos de mil).
 *
 * La respuesta incluye:
 *   - amount_origin        — cuánto debe enviar el cliente
 *   - amount_destination   — cuánto llega REALMENTE (recalculado por el motor)
 *   - target_destination_requested — el número que pidió el usuario
 *   - destination_adjusted — true si difiere del pedido en ≥ 0.01
 *   - destination_diff     — diferencia (llega - pedido)
 */
export async function quoteReverse({ amountDestination, origin = 'CLP', destination = 'BOB', rounding }) {
  const url = new URL(`${API}/quote/reverse`);
  url.searchParams.set('amount_destination', amountDestination);
  url.searchParams.set('origin', origin);
  url.searchParams.set('destination', destination);
  if (rounding !== undefined && rounding !== null) {
    url.searchParams.set('rounding', rounding);
  }
  const res = await axios.get(url.toString(), { timeout: 15000 });
  return res.data;
}

/**
 * Congela la tasa por sesión durante 10 minutos.
 * Útil para que el cliente que ya cotizó vea el mismo número hasta completar.
 */
export async function quoteLock({ amount, origin = 'CLP', destination = 'BOB', sessionId = null }) {
  const url = new URL(`${API}/quote/lock`);
  url.searchParams.set('amount', amount);
  url.searchParams.set('origin', origin);
  url.searchParams.set('destination', destination);
  if (sessionId) url.searchParams.set('session_id', sessionId);
  const res = await axios.post(url.toString(), null, { timeout: 15000 });
  return res.data;
}

/**
 * Recupera una cotización congelada por session_id.
 */
export async function quoteGetLocked(sessionId) {
  const res = await axios.get(`${API}/quote/locked/${sessionId}`, { timeout: 10000 });
  return res.data;
}
